'use client';
// ──────────────────────────────────────────────────────
// AiScanLoader — Premium AI orb animation for scan loading state
//
// Shown during the 'ai_processing' phase of a barcode scan.
// Keeps the user engaged while the backend fetches data and
// the minimum display timer (2200ms) is still running.
//
// Visual: soft floating orb with gradient glow, breathing scale,
// gentle rotation, blur halo — premium / kiosk-grade, no video.
// Reduced-motion: simplified static glow only.
// ──────────────────────────────────────────────────────
import React from 'react';
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion';

interface AiScanLoaderProps {
    visible: boolean;
}

export function AiScanLoader({ visible }: AiScanLoaderProps) {
    const prefersReduced = useReducedMotion();

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    key="ai-scan-loader"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.35, ease: 'easeInOut' }}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 120,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(255,255,255,0.96)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                    }}
                    dir="rtl"
                >
                    {/* Orb container */}
                    <div style={{ position: 'relative', width: 180, height: 180, marginBottom: 36 }}>

                        {/* Outer blur halo */}
                        {!prefersReduced && (
                            <motion.div
                                animate={{ scale: [1, 1.18, 1], opacity: [0.25, 0.5, 0.25] }}
                                transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
                                style={{
                                    position: 'absolute',
                                    inset: -20,
                                    borderRadius: '50%',
                                    background: 'radial-gradient(circle, rgba(167,139,250,0.35) 0%, rgba(99,102,241,0.15) 55%, transparent 100%)',
                                    filter: 'blur(20px)',
                                }}
                            />
                        )}

                        {/* Mid glow ring */}
                        {!prefersReduced && (
                            <motion.div
                                animate={{ scale: [1, 1.08, 1], opacity: [0.4, 0.7, 0.4] }}
                                transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut', delay: 0.3 }}
                                style={{
                                    position: 'absolute',
                                    inset: -4,
                                    borderRadius: '50%',
                                    background: 'radial-gradient(circle, rgba(196,181,253,0.5) 0%, rgba(139,92,246,0.25) 60%, transparent 100%)',
                                    filter: 'blur(10px)',
                                }}
                            />
                        )}

                        {/* Core orb */}
                        <motion.div
                            animate={prefersReduced ? {} : {
                                scale: [1, 1.05, 0.97, 1.04, 1],
                                rotate: [0, 8, -6, 10, 0],
                            }}
                            transition={{
                                duration: 5.5,
                                repeat: Infinity,
                                ease: 'easeInOut',
                            }}
                            style={{
                                position: 'absolute',
                                inset: 0,
                                borderRadius: '50%',
                                background: `
                                    radial-gradient(circle at 38% 34%,
                                        rgba(255,255,255,0.92) 0%,
                                        rgba(216,180,254,0.85) 28%,
                                        rgba(167,139,250,0.9) 52%,
                                        rgba(99,102,241,0.8) 74%,
                                        rgba(79,70,229,0.7) 100%
                                    )
                                `,
                                boxShadow: `
                                    0 0 40px 8px rgba(167,139,250,0.35),
                                    0 0 0 1px rgba(196,181,253,0.3),
                                    inset 0 2px 20px rgba(255,255,255,0.5)
                                `,
                                overflow: 'hidden',
                            }}
                        >
                            {/* Inner shimmer sweep */}
                            {!prefersReduced && (
                                <motion.div
                                    animate={{ x: ['−100%', '200%'] }}
                                    transition={{ duration: 2.2, repeat: Infinity, ease: 'linear', delay: 0.5 }}
                                    style={{
                                        position: 'absolute',
                                        top: -10,
                                        left: 0,
                                        width: '45%',
                                        height: '120%',
                                        background: 'linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.28) 50%, transparent 70%)',
                                        transform: 'skewX(-15deg)',
                                    }}
                                />
                            )}

                            {/* Subtle liquid blob inside orb */}
                            {!prefersReduced && (
                                <motion.div
                                    animate={{ scale: [0.7, 0.9, 0.75, 0.85, 0.7], x: [0, 12, -8, 14, 0], y: [0, -10, 8, -6, 0] }}
                                    transition={{ duration: 4.8, repeat: Infinity, ease: 'easeInOut' }}
                                    style={{
                                        position: 'absolute',
                                        top: '20%',
                                        left: '20%',
                                        width: '60%',
                                        height: '60%',
                                        borderRadius: '50%',
                                        background: 'radial-gradient(circle, rgba(236,72,153,0.22) 0%, rgba(167,139,250,0.12) 60%, transparent 100%)',
                                        filter: 'blur(8px)',
                                    }}
                                />
                            )}
                        </motion.div>

                        {/* Floating particle dots */}
                        {!prefersReduced && [0, 1, 2].map((i) => (
                            <motion.div
                                key={i}
                                animate={{
                                    y: [0, -18 - i * 6, 0],
                                    x: [0, (i % 2 === 0 ? 10 : -10) + i * 4, 0],
                                    opacity: [0, 0.7, 0],
                                    scale: [0.4, 1, 0.4],
                                }}
                                transition={{
                                    duration: 2.6 + i * 0.4,
                                    repeat: Infinity,
                                    ease: 'easeInOut',
                                    delay: i * 0.7,
                                }}
                                style={{
                                    position: 'absolute',
                                    top: `${30 + i * 15}%`,
                                    left: `${20 + i * 25}%`,
                                    width: 6 + i * 2,
                                    height: 6 + i * 2,
                                    borderRadius: '50%',
                                    background: i === 0
                                        ? 'rgba(216,180,254,0.9)'
                                        : i === 1
                                        ? 'rgba(99,102,241,0.8)'
                                        : 'rgba(236,72,153,0.7)',
                                    boxShadow: '0 0 6px 2px rgba(167,139,250,0.4)',
                                }}
                            />
                        ))}
                    </div>

                    {/* Label */}
                    <motion.p
                        animate={prefersReduced ? {} : { opacity: [0.7, 1, 0.7] }}
                        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                        style={{
                            fontSize: 17,
                            fontWeight: 600,
                            color: '#6d28d9',
                            letterSpacing: '-0.01em',
                            margin: 0,
                            fontFamily: "'Segoe UI', Tahoma, system-ui, sans-serif",
                        }}
                    >
                        جارٍ التحليل بالذكاء الاصطناعي…
                    </motion.p>

                    {/* Sub-label */}
                    <p style={{
                        marginTop: 8,
                        fontSize: 13,
                        color: '#a78bfa',
                        fontFamily: "'Segoe UI', Tahoma, system-ui, sans-serif",
                    }}>
                        يرجى الانتظار لحظة
                    </p>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
